import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { ChatDotsFill, XCircleFill, SendFill } from 'react-bootstrap-icons';
import axios from 'axios';
import './Chatbot.css';



export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [faqs, setFaqs] = useState([]);

  // Load FAQ content from the API the first time the chat is opened
  useEffect(() => {
    if (!isOpen || faqs.length > 0) return;
    axios.get(`${process.env.REACT_APP_CORE_API}/chatbot/faq`)
      .then(res => setFaqs(res.data || []))
      .catch(err => console.error('Failed to load chatbot FAQs:', err));
  }, [isOpen, faqs.length]);
  const [messages, setMessages] = useState([
    { text: "Hi! I'm here to help answer your questions. What would you like to know?", sender: "bot" }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [emailData, setEmailData] = useState({ name: '', email: '', question: '' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Score every entry and return the best match. A keyword phrase counts when all of its
  // words appear in the question, in any order, so "attorney account" matches "how does an
  // attorney get an account" and outweighs a bare "attorney" elsewhere.
  const findAnswer = (question) => {
    const words = new Set(question.toLowerCase().match(/[a-z0-9']+/g) || []);
    let best = null;
    let bestScore = 0;

    for (const faq of faqs) {
      const score = faq.keywords.reduce((sum, keyword) => {
        const parts = keyword.toLowerCase().match(/[a-z0-9']+/g) || [];
        return parts.length > 0 && parts.every(w => words.has(w)) ? sum + parts.length : sum;
      }, 0);
      if (score > bestScore) {
        bestScore = score;
        best = faq;
      }
    }
    return best?.answer || null;
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMessage = { text: inputMessage, sender: "user" };
    setMessages(prev => [...prev, userMessage]);

    const answer = findAnswer(inputMessage);

    if (answer) {
      setTimeout(() => {
        const botMessage = { text: answer, sender: "bot" };
        setMessages(prev => [...prev, botMessage]);
      }, 500);
    } else {
      setTimeout(() => {
        const botMessage = {
          text: "I don't have an answer to that question. Would you like to send this question to our support team via email?",
          sender: "bot",
          showEmailButton: true,
          userQuestion: inputMessage
        };
        setMessages(prev => [...prev, botMessage]);
      }, 500);
    }

    setInputMessage('');
  };

  const handleEmailFormOpen = (question) => {
    setEmailData({ ...emailData, question });
    setIsEmailFormOpen(true);
  };

  const handleSendEmail = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_CORE_API}/contact`, {
        name: emailData.name,
        email: emailData.email,
        message: emailData.question,
        source: 'chatbot'
      });
      setMessages(prev => [...prev, {
        text: "Thank you! Your question has been received. We'll get back to you soon!",
        sender: "bot"
      }]);
    } catch (err) {
      console.error('Support email failed:', err);
      setMessages(prev => [...prev, {
        text: "Sorry, something went wrong sending that. Please try again in a moment.",
        sender: "bot"
      }]);
    }

    setIsEmailFormOpen(false);
    setEmailData({ name: '', email: '', question: '' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <div
        className={`chatbot-button ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
      >
        <ChatDotsFill size={28} />
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h5>QMI Support</h5>
            <XCircleFill
              size={24}
              onClick={() => setIsOpen(false)}
              style={{ cursor: 'pointer' }}
            />
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, index) => (
              <div key={index}>
                <div className={`message ${msg.sender}`}>
                  {msg.text}
                </div>
                {msg.showEmailButton && (
                  <div className="email-prompt">
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handleEmailFormOpen(msg.userQuestion)}
                    >
                      Send to Support
                    </Button>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <Form.Control
              type="text"
              placeholder="Type your question..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <Button
              variant="primary"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
            >
              <SendFill />
            </Button>
          </div>
        </div>
      )}

      {/* Email Form Modal */}
      <Modal show={isEmailFormOpen} onHide={() => setIsEmailFormOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send Question to Support</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Your Name</Form.Label>
              <Form.Control
                type="text"
                value={emailData.name}
                onChange={(e) => setEmailData({ ...emailData, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Your Email</Form.Label>
              <Form.Control
                type="email"
                value={emailData.email}
                onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Your Question</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={emailData.question}
                onChange={(e) => setEmailData({ ...emailData, question: e.target.value })}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsEmailFormOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendEmail}
            disabled={!emailData.name || !emailData.email || !emailData.question}
          >
            Send
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
